/**
 * Surface the most useful single line we can from whatever an async
 * action throws at us — JS `Error`, a thrown string, or (the common
 * case with Supabase) a PostgREST/Postgres error.
 *
 * The naive `e instanceof Error ? e.message : fallback` pattern fails on
 * the most common case in this app: PostgREST returns a `PostgrestError`,
 * which is a *plain object* (`{ message, details, hint, code }`) — NOT an
 * `Error` instance — so `instanceof Error` is `false` and the toast shows
 * the literal fallback string with no insight at all. We pull `message`
 * directly (and append `details` / `code` when they add information) so
 * the operator sees the real reason (e.g. a `42501` RLS denial) instead
 * of a useless generic message.
 */
export function formatError(e: unknown, fallback = 'Something went wrong.'): string {
  if (typeof e === 'string') return e || fallback;
  if (e instanceof Error) return e.message || fallback;
  if (e && typeof e === 'object') {
    const obj = e as Record<string, unknown>;
    const message = typeof obj.message === 'string' ? obj.message.trim() : '';
    const details = typeof obj.details === 'string' ? obj.details.trim() : '';
    const hint = typeof obj.hint === 'string' ? obj.hint.trim() : '';
    const code = typeof obj.code === 'string' ? obj.code.trim() : '';
    const headline = message || details || hint;
    if (headline) {
      const suffix = details && details !== headline ? ` (${details})` : '';
      const codeSuffix = code && !headline.includes(code) ? ` [${code}]` : '';
      return headline + suffix + codeSuffix;
    }
  }
  return fallback;
}
